/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	TableContainer,
	Table,
	Thead,
	Tbody,
	Tfoot,
	Tr,
	Th,
	Td,
	Image,
	Box,
	Flex,
	Button,
	Text,
	Tag,
	TagCloseButton,
	TagLabel,
	FormControl,
	FormLabel,
	InputGroup,
	InputRightElement,
	FormHelperText,
	useDisclosure,
	Badge,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Field, useFormikContext } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/free-solid-svg-icons';
import { gql, useLazyQuery } from '@apollo/client';
import { CompanyBadge } from '../Other';
import CompanyName from '../CompanyName';
import LoadingTable from '../Table/Loading';
import Placeholder from '../../assets/images/logo-placeholder.png';
import c from '../../constant';
import useStore from '../../store';

const Q_DETAIL = gql`
	query get($id: String!) {
		getCompanyByCompanyId(id: $id) {
			id
			name
			nameKhmer
			logo
			url
			type
		}
	}
`;

const Q_PARTNERS = gql`
	query GET($companyId: String) {
		getCompanyAgentPartners(companyId: $companyId) {
			id
			model
			companyType
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			intermediary {
				id
				name
				nameKhmer
				type
				logo
			}
			createdAt
			active
			approvalId
			active
			inactiveAt
			inactiveById
			attachment
		}
	}
`;

function PartneredCompanyFormField({ dependency = null, name = null, label = null, required = false }) {
	const modal = useDisclosure();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const { t } = useTranslation('common');
	const { values } = useFormikContext();
	const [getDetail, { data: dataDetail }] = useLazyQuery(Q_DETAIL);
	const [getPartners, { loading: loadingPartners, data: dataPartners }] = useLazyQuery(Q_PARTNERS);

	useEffect(() => getPartners({ variables: { companyId: values[dependency] } }), [values[dependency]]);

	useEffect(() => values[name] && getDetail({ variables: { id: values[name] } }), [values[name]]);

	function Model({ model = null, color = 'orange' }) {
		switch (model) {
			case 'direct':
				return (
					<Badge variant="solid" colorScheme={color}>
						{t('Direct Sale')}
					</Badge>
				);
			case 'ref':
				return (
					<Badge variant="solid" colorScheme={color}>
						{t('Referral')}
					</Badge>
				);
			default:
				return null;
		}
	}

	return (
		<>
			<Field name={name}>
				{({ field, meta, form }) => (
					<>
						<Box mb="4">
							<FormControl id={name}>
								<FormLabel>
									{t(label)}
									{required && <span style={{ color: 'red' }}>*</span>}
								</FormLabel>
								<InputGroup size="md">
									<Flex alignItems="center" w="100%" borderRadius={c.borderRadius} h="48px" border="1px solid #CBD5E0">
										{dataDetail?.getCompanyByCompanyId && values[name] ? (
											<Tag
												size="lg"
												ml="2px"
												borderRadius={c.borderRadius}
												variant="solid"
												colorScheme="green"
												w="calc(100% - 75px)"
												h="42px"
											>
												<TagLabel w="100%" fontSize={18} pt="8px" pb="8px">
													<CompanyName company={dataDetail?.getCompanyByCompanyId} />
												</TagLabel>
												<TagCloseButton onClick={() => form?.setFieldValue(name, '')} />
											</Tag>
										) : (
											<Text fontSize="medium" fontWeight="medium" color="gray.600" ml={4} mr={4}>
												{t('Company')}
											</Text>
										)}
									</Flex>
									<InputRightElement width="4.5rem">
										<Button
											variant="solid"
											mr="4px"
											mt="8px"
											h="42px"
											borderRadius={c.borderRadius}
											leftIcon={
												<FontAwesomeIcon
													icon={faList}
													style={{
														cursor: 'pointer',
														fontSize: 16,
													}}
												/>
											}
											onClick={modal?.onOpen}
										>
											{t('List')}
										</Button>
									</InputRightElement>
								</InputGroup>
								{meta.touched && meta.error && (
									<FormHelperText id={`error-message-${name}`} color="red.400" fontSize="12px">
										{t(meta.error)}
									</FormHelperText>
								)}
							</FormControl>
						</Box>
						{modal?.isOpen && (
							<Modal isOpen={modal?.isOpen} onClose={modal?.onClose} size="3xl">
								<ModalOverlay />
								<ModalContent borderRadius={c.borderRadius}>
									<ModalHeader>{t('Partnered Company')}</ModalHeader>
									<ModalCloseButton />
									<ModalBody>
										<TableContainer>
											<Table variant="simple">
												<Thead>
													<Tr>
														<Th>{t('Logo')}</Th>
														<Th>{t('Company Name')}</Th>
														<Th>{t('Company Type')}</Th>
														<Th isNumeric>{t('Model')}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{loadingPartners && <LoadingTable column={3} />}
													{dataPartners?.getCompanyAgentPartners?.length <= 0 && (
														<Tr>
															<Td colSpan={3}>
																<Text>{t('No Data')}</Text>
															</Td>
														</Tr>
													)}
													{dataPartners?.getCompanyAgentPartners?.map((e, i) => {
														return (
															<Tr
																_hover={{ backgroundColor: 'gray.200' }}
																cursor="pointer"
																key={`company-row-${i}`}
																onClick={() => {
																	form?.setFieldValue(name, e?.company?.id);
																	modal?.onClose();
																}}
															>
																<Td pt="0" pb="0">
																	<Image
																		src={e?.company?.logo ? c.baseFilePath + e?.company?.logo : ''}
																		w="auto"
																		h="32px"
																		fallbackSrc={Placeholder}
																	/>
																</Td>
																<Td>{lang === 'kh' ? e?.company?.nameKhmer : e?.company?.name}</Td>
																<Td>
																	<CompanyBadge type={e?.company?.type} />
																</Td>
																<Td isNumeric>
																	<Model model={e?.model} />
																</Td>
															</Tr>
														);
													})}
												</Tbody>
												<Tfoot>
													<Tr>
														<Th>{t('Logo')}</Th>
														<Th>{t('Company Name')}</Th>
														<Th>{t('Company Type')}</Th>
														<Th isNumeric>{t('Model')}</Th>
													</Tr>
												</Tfoot>
											</Table>
										</TableContainer>
									</ModalBody>
								</ModalContent>
							</Modal>
						)}
					</>
				)}
			</Field>
		</>
	);
}

export default PartneredCompanyFormField;
